import React, {
  useContext,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { compose } from 'recompose';
import { observer } from 'mobx-react';
import format from 'string-template';

import LoadingPage from '../../../../../chat/components/LoadingPage/LoadingPage';
import EmptyView from '../../../../../chat/components/EmptyView';
import LeadConversationsContext, {
  ConversationChannel,
} from '../../../../context/LeadConversationsContext/LeadConversationsContext';
import UserContext from '../../../../../context/UserContext';
import ConversationBucketSelect from '../ConversationBucketSelect';
import ConversationChannelSelect from '../ConversationChannelSelect';
import LeadConversationPreview from '../LeadConversationPreview';

import {
  Container,
  ListHeader,
  ListItemsContainer,
  LoadMoreButton,
  NoResultsOnPage,
  StyledSearchBar,
} from './styles';
import texts from './texts.json';

const EMPTY_VALUE = '';

const LeadConversationList = () => {
  const {
    isReady,
    conversationList,
    isLoadingConversations,
    hasMoreConversations,
    loadMoreConversations,
    selectedBucket,
    activeLeadId,
    searchResults,
    handleSearch,
    selectedChannel,
    setActiveConversation,
    igConversationList,
    isLoadingIGConversations,
    hasMoreIGConversations,
    loadMoreIGConversations,
    conversationPagesLoaded,
    igConversationPagesLoaded,
  } = useContext(LeadConversationsContext);

  const { userDoc: { id: currentUserId } } = useContext(UserContext);
  const [searchValue, setSearchValue] = useState(EMPTY_VALUE);

  const handleConversationClick = useCallback((leadId) => {
    setActiveConversation(leadId, selectedChannel);
  }, [setActiveConversation, selectedChannel]);

  const handleSearchChange = useCallback((e) => {
    setSearchValue(e.target.value);
    handleSearch(e.target.value);
  }, [handleSearch]);

  const handleSearchClear = useCallback(() => {
    setSearchValue(EMPTY_VALUE);
    handleSearch(EMPTY_VALUE);
  }, [handleSearch]);

  const isIGChannel = selectedChannel === ConversationChannel.INSTAGRAM;
  const activeConversationList = isIGChannel ? igConversationList : conversationList;
  const activeHasMore = isIGChannel ? hasMoreIGConversations : hasMoreConversations;
  const activeLoadMore = isIGChannel ? loadMoreIGConversations : loadMoreConversations;
  const activeIsLoading = isIGChannel ? isLoadingIGConversations : isLoadingConversations;
  const pagesLoaded = isIGChannel ? igConversationPagesLoaded : conversationPagesLoaded;

  const isSearchActive = searchResults !== null;
  const displayList = isSearchActive ? searchResults : activeConversationList;

  const isLoadingMore = isSearchActive && activeIsLoading;
  const loadMoreLabel = useMemo(() => {
    if (!isSearchActive) return texts.loadMore;
    if (!activeIsLoading && activeHasMore) {
      return format(texts.searchMore, { count: pagesLoaded });
    }
    return texts.searching;
  }, [isSearchActive, activeIsLoading, activeHasMore, pagesLoaded]);

  const renderConversations = () => {
    if (!isReady || (!isSearchActive && activeIsLoading)) {
      return <LoadingPage fullHeight />;
    }

    if (displayList.length === 0 && !(isSearchActive && activeHasMore)) {
      return <EmptyView content={isSearchActive ? texts.noSearchResults : texts.noConversations} />;
    }

    return (
      <>
        {displayList.map((doc) => (
          <LeadConversationPreview
            key={doc.id}
            name={doc.leadName || doc.leadId}
            lastMessageText={isIGChannel ? doc.lastIgMessageText : doc.lastMessageText}
            lastMessageAt={isIGChannel ? doc.lastIgMessageAt : doc.lastMessageAt}
            lastMessageIsComment={isIGChannel ? false : doc.lastMessageIsComment}
            isActive={doc.leadId === activeLeadId}
            onClick={() => handleConversationClick(doc.leadId)}
            {...(!isIGChannel && {
              claimedBy: doc.claimedBy,
              claimedByUsername: doc.claimedByUsername,
              isClaimValid: doc.isClaimValid,
              useAutomatedSdr: doc.useAutomatedSdr,
            })}
            currentUserId={currentUserId}
            lastOutgoingIsAgentName={doc.lastOutgoingIsAgentName}
          />
        ))}
        {displayList.length === 0 && isSearchActive && activeHasMore && (
          <NoResultsOnPage>
            {texts.noResultsOnPage}
          </NoResultsOnPage>
        )}
        {activeHasMore && (
          <LoadMoreButton
            onClick={activeLoadMore}
            disabled={isLoadingMore}
          >
            {loadMoreLabel}
          </LoadMoreButton>
        )}
      </>
    );
  };

  return (
    <Container>
      <ListHeader>
        <ConversationBucketSelect />
        <ConversationChannelSelect />
        <StyledSearchBar
          key={`${selectedBucket}-${selectedChannel}`}
          value={searchValue}
          onChange={handleSearchChange}
          clearSearch={handleSearchClear}
          placeholder={texts.searchPlaceholder}
        />
      </ListHeader>
      <ListItemsContainer>
        {renderConversations()}
      </ListItemsContainer>
    </Container>
  );
};

export default compose(
  observer,
)(LeadConversationList);
